import React from 'react';

import { useTranslation } from 'react-i18next';
import { ButtonLinkType } from '../../graphql-fragments/ButtonLink';
import { getUrlFromVersatileLink } from '../../utils/sanity';
import { clsx } from '../../utils/utils';
import * as styles from './FundCard.module.scss';
import SmartLink from './SmartLink';
import TextLink from './TextLink';

export type FundCardProps = {
  title: string;
  text: string;
  button: ButtonLinkType;
  fundsInfoToShow?: 'metrics' | 'top5Assets';
  metrics?: {
    nav: string;
    YTD: number;
    twelveM: number;
  };
  investmentRisk?: string;
  assetTypes?: string;
  top5Holdings?: Array<{
    symbol: string;
    company: string;
    assets: number;
  }>;
  top5Issuers?: {
    companyLabel: string;
    issuers: Array<{
      company: string;
    }>;
  };
  isCarouselCard?: boolean;
  className?: string;
};

const FundCard = ({
  title,
  text,
  button,
  fundsInfoToShow,
  metrics,
  investmentRisk,
  assetTypes,
  top5Holdings,
  top5Issuers,
  isCarouselCard,
  className,
}: FundCardProps): React.ReactElement => {
  const { i18n, t } = useTranslation();

  const investmentRiskToBarsClass = {
    1: styles.oneBarFill,
    2: styles.twoBarFill,
    3: styles.threeBarFill,
    4: styles.fourBarFill,
    5: styles.fiveBarFill,
    6: styles.sixBarFill,
    7: styles.sevenBarFill,
  };

  return (
    <SmartLink
      to={getUrlFromVersatileLink(button)}
      className={clsx(styles.fundCardContainer, isCarouselCard && styles.carouselCard, className)}
      data-theme="light"
    >
      <h3 className={styles.title}>{title}</h3>
      <p className={clsx(styles.text, !fundsInfoToShow && styles.withMarginBottom)}>{text}</p>
      {fundsInfoToShow === 'metrics' && (
        <div className={styles.metricsContainer}>
          {metrics && (
            <div className={styles.metricsWrapper}>
              <div className={styles.metricsSquare}>
                <span className={styles.metric} title="Net Asset Value">
                  <strong>NAV: </strong>
                  {metrics.nav}
                </span>
              </div>
              {metrics.YTD && (
                <div className={styles.metricsSquare}>
                  <span className={styles.metric} title="Year to Date">
                    <strong>YTD: </strong>
                    {metrics.YTD + '%'}
                  </span>
                </div>
              )}
              {metrics.twelveM && (
                <div className={styles.metricsSquare}>
                  <span className={styles.metric}>
                    <strong>12M: </strong>
                    {metrics.twelveM + '%'}
                  </span>
                </div>
              )}
            </div>
          )}

          {investmentRisk && (
            <div className={styles.investmentRiskSection}>
              <h4 className={styles.investmentRiskTitle}>
                {t('investmentRisk.title', 'Risco do Investimento')}
              </h4>
              <div
                className={
                  styles.investmentRiskBarsContainer +
                  ' ' +
                  investmentRiskToBarsClass[investmentRisk]
                }
              >
                <div className={styles.bar}></div>
                <div className={styles.bar}></div>
                <div className={styles.bar}></div>
                <div className={styles.bar}></div>
                <div className={styles.bar}></div>
                <div className={styles.bar}></div>
                <div className={styles.bar}></div>
              </div>
              <div className={styles.investmentRiskLabels}>
                <span>{t('investmentRisk-low.title', 'Baixo')}</span>
                <span>{t('investmentRisk-high.title', 'Alto')}</span>
              </div>
            </div>
          )}
        </div>
      )}
      {fundsInfoToShow === 'top5Assets' && (
        <>
          {/* Show Issuers if assetTypes is 'issuers', otherwise show Holdings (default) */}
          {assetTypes === 'issuers'
            ? top5Issuers &&
              top5Issuers.issuers &&
              top5Issuers.issuers.length > 0 && (
                <div className={styles.top5HoldingsContainer}>
                  <h4 className={styles.top5HoldingsTitle}>Top 5 Issuers</h4>
                  <div className={styles.tableContainer}>
                    <div className={styles.tableTitlesContainer}>
                      <span className={styles.tableTitle}>
                        {top5Issuers.companyLabel || 'Company'}
                      </span>
                    </div>
                    <div className={styles.tableRowsContainer}>
                      {top5Issuers.issuers.map((issuer, i) => (
                        <React.Fragment key={i}>
                          <div className={styles.tableRow} key={i}>
                            <span className={styles.tableRowInfo}>{issuer.company}</span>
                          </div>
                          {i !== top5Issuers.issuers.length - 1 && <div className={styles.divider}></div>}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              )
            : top5Holdings &&
              top5Holdings.length > 0 && (
                <div className={styles.top5HoldingsContainer}>
                  <h4 className={styles.top5HoldingsTitle}>Top 5 Holdings</h4>
                  <div className={styles.tableContainer}>
                    <div className={styles.tableTitlesContainer}>
                      <span className={clsx(styles.tableTitle, styles.symbol)}>Symbol</span>
                      <span className={styles.tableTitle}>Company</span>
                      <span className={clsx(styles.tableTitle, styles.assets)}>% Assets</span>
                    </div>
                    <div className={styles.tableRowsContainer}>
                      {top5Holdings.map((holding, i) => (
                        <React.Fragment key={i}>
                          <div className={styles.tableRow} key={i}>
                            <span className={clsx(styles.tableRowInfo, styles.symbol)}>
                              {holding.symbol}
                            </span>
                            <span className={styles.tableRowInfo}>{holding.company}</span>
                            <span className={clsx(styles.tableRowInfo, styles.assets)}>
                              {holding.assets}%
                            </span>
                          </div>
                          {i !== top5Holdings.length - 1 && <div className={styles.divider}></div>}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              )}
        </>
      )}
      <TextLink styleOnly withArrowIcon className={styles.link}>
        {button.text}
      </TextLink>
    </SmartLink>
  );
};

export default FundCard;
